/**
 * Правила кольору карток (прототип v4 cardTone · fmatch): перше правило, умова якого справджується для запису, фарбує картку —
 * тло color-mix(C 13%, --surface), рамка color-mix(C 45%, --line). Чисті функції.
 */
import type { FieldValue, IRecord } from '@platform/core/types';
import type { ICardColourRule } from './ICardColourRule';
import type { ICardTone } from './ICardTone';

/** Кольори тла правил (прототип v4 CARD_COLORS): червоний, помаранчевий, жовтий, зелений, синій, фіолетовий, сірий — ключ (назва) і колір. */
export const CARD_COLORS: readonly { key: string; hex: string }[] = [
  { key: 'red', hex: '#dc2626' }, { key: 'orange', hex: '#ea580c' }, { key: 'yellow', hex: '#ca8a04' }, { key: 'green', hex: '#16a34a' },
  { key: 'blue', hex: '#2563eb' }, { key: 'purple', hex: '#7c3aed' }, { key: 'grey', hex: '#64748b' },
];

/** Колір правила: збережена назва кольору прототипу (red…) — її відтінок, інакше саме значення (#rrggbb). */
export function colorHex(color: string): string {
  return CARD_COLORS.find((c) => c.key === color)?.hex ?? color;
}

/** Значення поля запису для порівняння: статус і назва з конверта; людина — ідентифікатор та ім'я; теги — імена. */
function valuesOf(record: IRecord, field: string): string[] {
  if (field === 'status') return record.status ? [record.status] : [];
  if (field === 'title') return [record.title];
  if (field === 'tags') return record.tags;
  const raw: FieldValue | undefined = record.values[field] ?? (record.ext[field] as FieldValue | undefined);
  if (raw === null || raw === undefined || raw === '') return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'object') return 'id' in raw ? [String(raw.id), String((raw as { name?: string }).name ?? '')] : [];
  return [String(raw)];
}

/** Порівняння «менше / більше»: числа — як числа, решта (дати ISO) — як рядки; `today` — сьогоднішня дата. */
function compare(a: string, b: string): number {
  const x = Number(a);
  const y = Number(b);
  if (a.trim() !== '' && b.trim() !== '' && !Number.isNaN(x) && !Number.isNaN(y)) return x - y;
  return a.slice(0, 10) < b ? -1 : a.slice(0, 10) > b ? 1 : 0;
}

/** Чи справджується умова правила для запису (`today` — дата `today`). */
export function ruleMatches(record: IRecord, rule: Pick<ICardColourRule, 'field' | 'op' | 'value'>, today: string): boolean {
  const values = valuesOf(record, rule.field);
  const target = rule.value.trim().toLowerCase() === 'today' ? today : rule.value.trim();
  const low = target.toLowerCase();
  switch (rule.op) {
    case 'empty': return values.length === 0;
    case 'filled': return values.length > 0;
    case 'eq': case 'has': return values.some((v) => v.toLowerCase() === low);
    case 'neq': return !values.some((v) => v.toLowerCase() === low);
    case 'contains': return values.some((v) => v.toLowerCase().includes(low));
    case 'lt': return values.length > 0 && compare(values[0]!, target) < 0;
    case 'gt': return values.length > 0 && compare(values[0]!, target) > 0;
    default: return false;
  }
}

/** Тон картки за першим правилом, що збіглося; немає — undefined. */
export function cardTone(record: IRecord, rules: readonly ICardColourRule[] | undefined, today: string): ICardTone | undefined {
  const rule = rules?.find((r) => r.field && r.color && ruleMatches(record, r, today));
  return rule ? toneOf(colorHex(rule.color)) : undefined;
}

/** Тло й рамка картки для кольору. */
export function toneOf(color: string): ICardTone {
  return { bg: `color-mix(in srgb, ${color} 13%, var(--surface))`, bd: `color-mix(in srgb, ${color} 45%, var(--line))` };
}

/** Правила кольору з визначення збереженого подання (вільний JSON сервера): лише об'єкти з рядковими полем, умовою й кольором. */
export function cardRulesOf(definition: unknown): ICardColourRule[] {
  const raw = (definition as { cardRules?: unknown } | null | undefined)?.cardRules;
  if (!Array.isArray(raw)) return [];
  return raw.filter((r): r is ICardColourRule => !!r && typeof r === 'object' && typeof (r as ICardColourRule).field === 'string'
    && typeof (r as ICardColourRule).op === 'string' && typeof (r as ICardColourRule).color === 'string')
    .map((r) => ({ field: r.field, op: r.op, value: typeof r.value === 'string' ? r.value : '', color: r.color }));
}
